import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { ItineraryStatusSchema } from './itineraries.js';

/**
 * Admin dashboard Phase 2 aggregates - open pipeline value, top agents
 * (WON MTD), and upcoming itinerary departures. Scope follows the same
 * RBAC rules as leads list / agent-target stats.
 */

export const DashboardPeriodSchema = z.enum(['7d', '30d', 'mtd']);
export type DashboardPeriod = z.infer<typeof DashboardPeriodSchema>;

export const DashboardInsightsQuerySchema = z.object({
  /** Chart window - defaults to current calendar month (IST). */
  period: DashboardPeriodSchema.default('mtd'),
});
export type DashboardInsightsQuery = z.infer<typeof DashboardInsightsQuerySchema>;

export const DashboardPipelineCurrencyTotalSchema = z.object({
  currencyCode: z.string(),
  amount: z.number().nonnegative(),
  leadCount: z.number().int().nonnegative(),
});
export type DashboardPipelineCurrencyTotal = z.infer<typeof DashboardPipelineCurrencyTotalSchema>;

export const DashboardTopAgentSchema = z.object({
  userId: UuidSchema,
  fullName: z.string(),
  wonLeadCount: z.number().int().nonnegative(),
  /** Sum of WON lead `budgetAmount` in the MTD window (same basis as agent targets). */
  achievementAmount: z.number().nonnegative(),
});
export type DashboardTopAgent = z.infer<typeof DashboardTopAgentSchema>;

export const DashboardUpcomingDepartureSchema = z.object({
  itineraryId: UuidSchema,
  /** Human label - lead reference, legacy AG id, or itinerary title. */
  label: z.string(),
  customerName: z.string().nullable(),
  destination: z.string(),
  travelStartDate: z.string(),
  pax: z.number().int().nonnegative(),
  status: ItineraryStatusSchema,
});
export type DashboardUpcomingDeparture = z.infer<typeof DashboardUpcomingDepartureSchema>;

export const DashboardDailyCountSchema = z.object({
  /** YYYY-MM-DD (IST calendar day). */
  date: z.string(),
  count: z.number().int().nonnegative(),
});
export type DashboardDailyCount = z.infer<typeof DashboardDailyCountSchema>;

export const DashboardLeadSourceCountSchema = z.object({
  sourceId: UuidSchema,
  sourceName: z.string(),
  count: z.number().int().nonnegative(),
});
export type DashboardLeadSourceCount = z.infer<typeof DashboardLeadSourceCountSchema>;

export const DashboardDestinationCountSchema = z.object({
  destinationId: UuidSchema,
  destinationName: z.string(),
  count: z.number().int().nonnegative(),
});
export type DashboardDestinationCount = z.infer<typeof DashboardDestinationCountSchema>;

export const DashboardOutcomesMtdSchema = z.object({
  won: z.number().int().nonnegative(),
  lost: z.number().int().nonnegative(),
});
export type DashboardOutcomesMtd = z.infer<typeof DashboardOutcomesMtdSchema>;

export const DashboardBookingsSummarySchema = z.object({
  bookingCount: z.number().int().nonnegative(),
  /** Sum of `grandTotalPaise` / 100 for non-draft, non-cancelled bookings in the period. */
  revenueInr: z.number().nonnegative(),
});
export type DashboardBookingsSummary = z.infer<typeof DashboardBookingsSummarySchema>;

export const DashboardSystemHealthSchema = z.object({
  smtpConfigured: z.boolean(),
  emailChannelActive: z.boolean(),
  failedNotifications7d: z.number().int().nonnegative(),
  successfulEmails7d: z.number().int().nonnegative(),
  /** Derived status for the ops tile - verify is on-demand in settings. */
  status: z.enum(['healthy', 'degraded', 'not_configured']),
});
export type DashboardSystemHealth = z.infer<typeof DashboardSystemHealthSchema>;

export const DashboardInsightsResponseSchema = z.object({
  /** Selected chart window key echoed back for the UI. */
  period: DashboardPeriodSchema,
  /** Inclusive IST calendar bounds used for period-scoped charts. */
  periodStart: z.string(),
  periodEnd: z.string(),
  openPipeline: z.object({
    leadCount: z.number().int().nonnegative(),
    totalsByCurrency: z.array(DashboardPipelineCurrencyTotalSchema),
  }),
  topAgents: z.array(DashboardTopAgentSchema),
  upcomingDepartures: z.array(DashboardUpcomingDepartureSchema),
  /** New leads created per IST day within the selected period. */
  leadsCreatedTrend: z.array(DashboardDailyCountSchema),
  /** Top query sources by lead count in the selected period. */
  leadsBySource: z.array(DashboardLeadSourceCountSchema),
  /** Top destinations by lead count in the selected period. */
  leadsByDestination: z.array(DashboardDestinationCountSchema),
  /** WON / LOST files closed in the selected period window. */
  outcomesMtd: DashboardOutcomesMtdSchema,
  /** Real booking count + revenue in the selected period (not budget proxies). */
  bookingsSummary: DashboardBookingsSummarySchema,
  /** Admin-only email / notification health; null for other roles. */
  systemHealth: DashboardSystemHealthSchema.nullable(),
  generatedAt: IsoDateTimeSchema,
});
export type DashboardInsightsResponse = z.infer<typeof DashboardInsightsResponseSchema>;
